import React, { useEffect, useState, useCallback } from 'react';
import SingleStackChart from '../SingleStackChart/index-beta';
import * as Ui from './styles';
import { performQuery } from './textSearch';
import { conditionallyPair } from '../../../../../util/arrays';
import { BAR } from 'src/components/Charts/types';
import { SIMPLECROSS_CARD } from 'src/sections/GroupCards/cardTypes';
/**
 * renderiza uma coluna com um grafico de cruzamento simples
 */
function renderCol(
  data,
  chartFontSize,
  legendsFilter,
  legendsFilterStrategy,
  legendsFilterHandler,
  theme,
  chartColors,
  themeTitle,
  updateSimpleCrossChartColors,
  colorPickerColors,
  colIndex,
  error,
  chartRef,
  chartContainerRef,
  onUpdateScrollView,
  rowIndex,
) {
  if (data) {
    return (
      <Ui.ColumnChartContainer>
        <SingleStackChart
          data={data}
          chartFontSize={chartFontSize}
          legendsFilter={legendsFilter}
          legendsFilterStrategy={legendsFilterStrategy}
          legendsFilterHandler={legendsFilterHandler}
          theme={theme}
          colors={chartColors}
          themeTitle={themeTitle}
          colorPickerColors={colorPickerColors}
          updateChartColors={updateSimpleCrossChartColors}
          rowIndex={rowIndex}
          colIndex={colIndex}
          error={error}
          chartRef={chartRef}
          chartContainerRef={chartContainerRef}
          onUpdateScrollView={onUpdateScrollView}
          chartType={BAR}
          cardType={SIMPLECROSS_CARD}
        />
      </Ui.ColumnChartContainer>
    );
  }
  return <></>;
}

/**
 * renderiza uma "linha" com dois graficos de cruz simples
 */
function renderRow(
  chartFontSize,
  pairData,
  legendsFilter,
  legendsFilterStrategy,
  legendsFilterHandler,
  theme,
  colors,
  themeTitle,
  updateSimpleCrossChartColors,
  colorPickerColors,
  error,
  chartRef,
  chartContainerRef,
  onUpdateScrollView,
  rowIndex,
) {
  const [firstData, secondData] = pairData;
  // A propiedade key utilizada aqui foi necessária devido o map feito no componente pai e usando a função
  // renderRow para gerar os componentes que serão os itens dessa listagem.

  // A propriedade key provoca a geração de uma nova intância do componente referenciado com a propriedade Key
  // apenas quando há uma mudança nos dados únicos atribuídos à mesma propriedade
  return (
    <Ui.RowChartContainer key={`${JSON.stringify(pairData)}`}>
      {renderCol(
        firstData,
        chartFontSize,
        legendsFilter,
        legendsFilterStrategy,
        legendsFilterHandler,
        theme,
        colors,
        themeTitle,
        updateSimpleCrossChartColors,
        colorPickerColors,
        0,
        error,
        chartRef,
        chartContainerRef,
        onUpdateScrollView,
        rowIndex,
      )}
      {renderCol(
        secondData,
        chartFontSize,
        legendsFilter,
        legendsFilterStrategy,
        legendsFilterHandler,
        theme,
        colors,
        themeTitle,
        updateSimpleCrossChartColors,
        colorPickerColors,
        1,
        error,
        chartRef,
        chartContainerRef,
        onUpdateScrollView,
        rowIndex,
      )}
    </Ui.RowChartContainer>
  );
}

/**
 * divide o payload dos resultado do cruzamento simples
 * em diferentes perguntas cruzadas. Este metodo foi criado
 * para que cada SingleStackChart recebe os dados com uma pergunta
 * cruzada
 */
function splitByPerguntaCruzada(data) {
  const pergunta_principal = data.perguntas_principais[0];
  /**
   * [{pesquisa, pergunta_principal:{id,enunciado,alternativas,pergunta_cruzada} }]
   */
  const resultados = pergunta_principal.perguntas_cruzadas.map(
    (pergunta_cruzada) => ({
      pesquisa: data.pesquisa,
      pergunta_principal: {
        ...pergunta_principal,
        pergunta_cruzada,
      },
    }),
  );

  return resultados;
}

const MultiStackChart = ({
  chartFontSize,
  data,
  colors,
  colorPickerColors,
  legendsFilter,
  legendsFilterStrategy,
  legendsFilterHandler,
  theme,
  themeTitle,
  updateSimpleCrossChartColors,
  error,
  chartRef,
}) => {
  const chartContainerRef = React.createRef(null);
  const [dataToShow, setDataToShow] = useState(legendsFilter ? {} : data);
  /**
   * escuta mudanças nas props de dados e no filtro de busca,
   * caso algum desses dois mudem a busca eh realizada novamente.
   */
  useEffect(() => {
    const filtered = performQuery(data, legendsFilter, legendsFilterStrategy);
    setDataToShow(filtered);
  }, [data, legendsFilterStrategy, legendsFilter]);

  const resultados = splitByPerguntaCruzada(dataToShow);

  /**
   * agrupe em par apenas as perguntas cruzadas que possuirem
   * menos de 10 alternativas.
   */
  const shouldGroup = (resultado) => resultado.pergunta_principal.pergunta_cruzada.alternativas.length < 10;

  const pairs = conditionallyPair(resultados, shouldGroup);

  // Atualiza a posição do scroll ao ré-renderizar o gráfico
  const onUpdateScrollView = useCallback(() => {
    if (chartContainerRef.current) {
      chartContainerRef.current.calledAfterUpdate = true;
    }
  }, [chartContainerRef]);

  // Keep scroll-y state and dashboard scroll-y state on nested component re-render
  useEffect(() => {
    const currentRef = chartContainerRef.current;
    const eventName = 'scroll';

    if (currentRef) {
      const handleScrollByRef = (reference) => {
        let flag = false;

        return () => {
          const isConsistentScroll = Math.floor(
            (reference.prevScrollTop || reference.scrollTop) / reference.scrollTop,
          ) <= 1;

          if (isConsistentScroll) {
            // eslint-disable-next-line no-param-reassign
            reference.prevScrollTop = reference.scrollTop;
          }

          if (!currentRef.calledAfterUpdate && !flag && isConsistentScroll) {
            // eslint-disable-next-line no-param-reassign
            reference.prevScrollTop = reference.scrollTop;
            flag = false;
            return;
          }

          // eslint-disable-next-line no-restricted-properties
          if (!window.isNaN(reference.prevScrollTop)
            && !isConsistentScroll
          ) {
            flag = true;
            currentRef.calledAfterUpdate = false;
            // eslint-disable-next-line no-param-reassign
            reference.scrollTop = reference.prevScrollTop;
            // eslint-disable-next-line no-param-reassign
            reference.prevScrollTop = reference.scrollTop;
          }
        };
      };

      const handleSimpleCrossScroll = handleScrollByRef(currentRef);

      currentRef.addEventListener(eventName, handleSimpleCrossScroll);
      return () => {
        currentRef.removeEventListener(eventName, handleSimpleCrossScroll);
      };
    }
  }, [chartContainerRef]);

  const scrollingArgs = pairs.length > 1 ? [chartContainerRef, onUpdateScrollView] : [undefined, undefined];

  return (
    <div
      ref={chartContainerRef}
      style={{ width: '100%', overflow: 'scroll' }}
    >
      {
        pairs.map((pair, rowIndex) => renderRow(
          chartFontSize,
          pair,
          legendsFilter,
          legendsFilterStrategy,
          legendsFilterHandler,
          { ...theme },
          colors,
          themeTitle,
          updateSimpleCrossChartColors,
          colorPickerColors,
          error,
          chartRef,
          ...scrollingArgs,
          rowIndex,
        ))
      }
    </div>
  );
};

export default MultiStackChart;
